/**
 * Clipboard Helper Utility
 * 
 * Provides safe clipboard operations with permission checks and user-friendly error handling.
 * 
 * Issue #10: Clipboard API Not Checking Permissions
 * - Checks clipboard API availability
 * - Handles permission denials gracefully
 * - Provides user-friendly error messages
 * - Offers fallback UI for manual copy
 * 
 * @module clipboardHelper
 */

/**
 * Result of a clipboard operation
 */
export interface ClipboardResult {
  success: boolean;
  error?: string;
  needsManualCopy?: boolean;
}

/**
 * Options for clipboard operations
 */
export interface ClipboardOptions {
  featureName?: string;
  showSuccessMessage?: boolean;
  successDuration?: number; // milliseconds
}

/**
 * Check if the Clipboard API is available in the current browser
 * 
 * @returns {boolean} True if clipboard API is available
 */
export const isClipboardAvailable = (): boolean => {
  return typeof navigator !== 'undefined' &&
    typeof navigator.clipboard !== 'undefined' &&
    typeof navigator.clipboard.writeText === 'function';
};

/**
 * Check clipboard write permission
 * 
 * @returns {Promise<boolean>} True if permission is granted or prompt
 */
export const checkClipboardPermission = async (): Promise<boolean> => {
  // Check if Permissions API is available
  if (!navigator.permissions) {
    // Permissions API not available, assume permission is granted
    // (will fail later if actually denied)
    return true;
  }

  try {
    // Query clipboard-write permission
    const result = await navigator.permissions.query({
      name: 'clipboard-write' as PermissionName
    });

    return result.state === 'granted' || result.state === 'prompt';
  } catch (error) {
    // Permission query failed, assume permission is available
    // (will fail later if actually denied)
    console.warn('Clipboard permission query failed:', error);
    return true;
  }
};

/**
 * Safely copy text to clipboard with permission checks and error handling
 * 
 * @param {string} text - Text to copy to clipboard
 * @param {ClipboardOptions} options - Optional configuration
 * @returns {Promise<ClipboardResult>} Result of the clipboard operation
 * 
 * @example
 * ```typescript
 * const result = await copyToClipboard(text, { featureName: 'Translation' });
 * if (result.success) {
 *   setSuccessMessage('Copied to clipboard!');
 * } else if (result.needsManualCopy) {
 *   setError(result.error);
 *   // Show manual copy UI
 * } else {
 *   setError(result.error);
 * }
 * ```
 */
export const copyToClipboard = async (
  text: string,
  options: ClipboardOptions = {}
): Promise<ClipboardResult> => {
  const { featureName = 'Feature' } = options;

  // Validate input
  if (!text || text.trim().length === 0) {
    return {
      success: false,
      error: 'No content to copy to clipboard.',
      needsManualCopy: false,
    };
  }

  // Try modern Clipboard API first (may be blocked in Office add-in sandboxed iframe)
  if (isClipboardAvailable()) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } catch {
      // Fall through to execCommand fallback
      console.warn(`[${featureName}] navigator.clipboard blocked — trying execCommand fallback`);
    }
  }

  // Fallback: document.execCommand('copy') — works in sandboxed iframes (Office add-ins)
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    if (success) return { success: true };
  } catch (error: unknown) {
    console.error(`[${featureName}] execCommand clipboard fallback failed:`, error);
  }

  return {
    success: false,
    error: 'Could not copy to clipboard. Please copy the text manually.',
    needsManualCopy: true,
  };
};

/**
 * Copy text to clipboard and manage UI state
 * 
 * This is a convenience function that handles common UI patterns:
 * - Sets copied state to true on success
 * - Resets copied state after duration
 * - Sets error state on failure
 * 
 * @param {string} text - Text to copy
 * @param {Function} setCopied - State setter for copied status
 * @param {Function} setError - State setter for error message
 * @param {ClipboardOptions} options - Optional configuration
 * @returns {Promise<boolean>} True if copy was successful
 * 
 * @example
 * ```typescript
 * const [copied, setCopied] = useState(false);
 * const [error, setError] = useState<string | null>(null);
 * 
 * const handleCopy = async () => {
 *   await copyWithUIState(text, setCopied, setError, { 
 *     featureName: 'Translation',
 *     successDuration: 2000 
 *   });
 * };
 * ```
 */
export const copyWithUIState = async (
  text: string,
  setCopied: (value: boolean) => void,
  setError: (error: string | null) => void,
  options: ClipboardOptions = {}
): Promise<boolean> => {
  const { successDuration = 2000 } = options;

  const result = await copyToClipboard(text, options);

  if (result.success) {
    setCopied(true);
    setError(null);

    // Reset copied state after duration
    setTimeout(() => setCopied(false), successDuration);

    return true;
  } else {
    setCopied(false);
    setError(result.error || 'Failed to copy to clipboard');

    return false;
  }
};

/**
 * Copy text to clipboard with index-based UI state (for lists)
 * 
 * @param {string} text - Text to copy
 * @param {number} index - Index of the item being copied
 * @param {Function} setCopied - State setter for copied index
 * @param {Function} setError - State setter for error message
 * @param {ClipboardOptions} options - Optional configuration
 * @returns {Promise<boolean>} True if copy was successful
 * 
 * @example
 * ```typescript
 * const [copied, setCopied] = useState<number | null>(null);
 * const [error, setError] = useState<string | null>(null);
 * 
 * const handleCopy = async (text: string, index: number) => {
 *   await copyWithIndexedUIState(text, index, setCopied, setError, { 
 *     featureName: 'ContentSuggestions' 
 *   });
 * };
 * ```
 */
export const copyWithIndexedUIState = async (
  text: string,
  index: number,
  setCopied: (value: number | null) => void,
  setError: (error: string | null) => void,
  options: ClipboardOptions = {}
): Promise<boolean> => {
  const { successDuration = 2000 } = options;

  const result = await copyToClipboard(text, options);

  if (result.success) {
    setCopied(index);
    setError(null);

    // Reset copied state after duration
    setTimeout(() => setCopied(null), successDuration);

    return true;
  } else {
    setCopied(null);
    setError(result.error || 'Failed to copy to clipboard');

    return false;
  }
};

/**
 * Get a user-friendly error message for clipboard failures
 * 
 * @param {any} error - The error object
 * @returns {string} User-friendly error message
 */
export const getClipboardErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'Failed to copy to clipboard. Please try again.';
  }

  const errorName = error instanceof Error ? error.name : '';
  if (errorName === 'NotAllowedError') {
    return 'Clipboard access denied. Please check your browser permissions.';
  }

  if (errorName === 'SecurityError') {
    return 'Security restrictions prevent clipboard access. Please copy manually.';
  }

  if (errorName === 'TypeError') {
    return 'Clipboard API is not supported in this browser.';
  }

  return 'Failed to copy to clipboard. Please try again or copy manually.';
};

